import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { t } from '@vk-hr-tek/core/translations';

import { ThunkExtra } from '@app/store';
import { VacationsService } from '@app/gen/vacations';
import { FilterSelectOption } from '@app/gen/policy';
import {
  CompanyEventsStat,
  CompanyGroupUsers,
  GetCompanyEmployeesParams,
  GetCompanyEmployeesRequest,
  GetCompanyPositionsParams,
  GetCompanyPositionsRequest,
  SettingsService,
} from '@app/gen/settings';
import { Entity } from '@app/types';
import { GetCompanyEmployeesResponse } from '@app/gen/organization';

import { EditScheduleDto } from '../../../dto';
import { SettingsState, SettingsWithRootState } from '../../settings.state';

export const getHasPositions = createAsyncThunk<
  FilterSelectOption[],
  {
    body?: GetCompanyPositionsRequest;
    params: GetCompanyPositionsParams;
  },
  ThunkExtra
>(
  'editVacation/getHasPositions',
  async ({ body, params }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const result = await service.getCompanyPositions({
        params,
        body: {
          pagination: {
            limit: 1,
            offset: 0,
          },
          filters: {
            ...body?.filters,
            query: '',
          },
        },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getHasEmployees = createAsyncThunk<
  GetCompanyEmployeesResponse,
  {
    params: GetCompanyEmployeesParams;
    body?: GetCompanyEmployeesRequest;
  },
  ThunkExtra
>(
  'editVacation/getHasEmployees',
  async ({ body, params }, { rejectWithValue, extra: { inject } }) => {
    try {
      const service = inject(SettingsService);

      const result = await service.getCompanyEmployees({
        params,
        body: {
          pagination: {
            limit: 1,
            offset: 0,
          },
          filters: {
            ...body?.filters,
            query: '',
          },
        },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const editSchedule = createAsyncThunk<
  void,
  {
    values: EditScheduleDto;
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra<SettingsWithRootState>
>(
  'editVacation/editSchedule',
  async (
    { values, actions },
    { dispatch, rejectWithValue, extra: { inject } },
  ) => {
    try {
      const {
        type,
        companyName,
        year,
        scheduleId,
        excludeEmployees,
        includeAllNewEmployees,
        includeEmployees,
        includePositions,
        includeUnits,
      } = values;

      const service = inject(VacationsService);

      await service.updateVacationSchedule({
        params: {
          schedule_id: scheduleId,
        },
        body: {
          exclude_employees: excludeEmployees,
          include_all_new_employees: includeAllNewEmployees,
          include_employees: includeEmployees,
          include_positions: includePositions,
          include_units: includeUnits,
        },
      });

      if (type === 'employees' || includeAllNewEmployees) {
        dispatch(
          showCustomNotification([
            {
              title: `${t('settings.vacations.edit.addEmployeesNotification')}${includeEmployees?.length ? ` (${includeEmployees.length})` : ''}`,
              type: 'info',
              subtitle: companyName,
              message: `График отпусков ${year} год`,
            },
          ]),
        );
      }

      if (type === 'units' && includeUnits?.length) {
        dispatch(
          showCustomNotification([
            {
              title: `Подразделение добавлено в планирование (${includeUnits.length})`,
              type: 'info',
              subtitle: companyName,
              message: `График отпусков ${year} год`,
            },
          ]),
        );
      }

      if (excludeEmployees?.length) {
        dispatch(
          showCustomNotification([
            {
              title: `${t('settings.vacations.edit.excludeEmployeesNotification')} (${excludeEmployees?.length})`,
              type: 'info',
              subtitle: companyName,
              message: `График отпусков ${year} год`,
            },
          ]),
        );
      }

      actions.resolve(null);
    } catch (err) {
      const { type, companyName, year } = values;

      dispatch(
        showCustomNotification([
          {
            title:
              type === 'units'
                ? 'Не удалось добавить подразделение'
                : t('settings.vacations.edit.addEmployeesErrorNotification'),
            type: 'error',
            subtitle: companyName,
            message: `График отпусков ${year} год`,
          },
        ]),
      );

      actions.reject({ [FORM_ERROR]: err });

      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsVacationsEditReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getHasPositions.pending, (state) => {
    state.vacationEdit.positions.status = 'loading';
  });
  builder.addCase(getHasPositions.fulfilled, (state, { payload }) => {
    state.vacationEdit.positions.status = 'complete';
    state.vacationEdit.positions.hasPositions = !!payload.length;
  });
  builder.addCase(getHasPositions.rejected, (state) => {
    state.vacationEdit.positions.status = 'failed';
    state.vacationEdit.positions.hasPositions = false;
  });

  builder.addCase(getHasEmployees.pending, (state) => {
    state.vacationEdit.employees.status = 'loading';
  });
  builder.addCase(getHasEmployees.fulfilled, (state, { payload }) => {
    state.vacationEdit.employees.status = 'complete';
    state.vacationEdit.employees.hasEmployees = !!payload.employees.length;
  });
  builder.addCase(getHasEmployees.rejected, (state) => {
    state.vacationEdit.employees.status = 'failed';
    state.vacationEdit.employees.hasEmployees = false;
  });
};
